import { useId, useMemo } from 'react';

import {
	FASTSHIFT_ASSESSMENT_RISKS,
	type FastshiftAssessmentResult,
	type FastshiftDcAssessmentStatus,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import Lozenge from '@atlaskit/lozenge';
import { Box, Grid, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { getAssessmentStatusConfig } from '@/common/utils/get-assessment-status-config';

import { AssessmentOverviewCard } from './assessment-overview-card';
import { messages } from './messages';

export type AssessmentOverviewSectionProps = {
	results: FastshiftAssessmentResult[];
	isLoading: boolean;
	assessedAt?: string | null;
	assessmentStatus?: FastshiftDcAssessmentStatus | null;
};

const styles = cssMap({
	grid: {
		gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
		gap: token('space.150'),
	},
});

export const AssessmentOverviewSection = ({
	results,
	isLoading,
	assessedAt,
	assessmentStatus,
}: AssessmentOverviewSectionProps) => {
	const { formatMessage, formatDate, formatNumber } = useIntl();
	const headingId = useId();
	const status = getAssessmentStatusConfig(assessmentStatus ?? null);
	const assessmentDate = assessedAt ? new Date(assessedAt) : null;
	const date =
		assessmentDate && !Number.isNaN(assessmentDate.getTime())
			? formatDate(assessmentDate, { year: 'numeric', month: 'short', day: 'numeric' })
			: '—';
	const metrics = useMemo(
		() =>
			[
				{
					key: 'total',
					label: messages.totalChecks,
					value: results.length,
					appearance: 'neutral',
					icon: StatusInformationIcon,
					iconAppearance: 'gray',
				},
				{
					key: 'attention',
					label: messages.needsAttention,
					value: results.filter(
						({ impact }) =>
							impact?.status === FASTSHIFT_ASSESSMENT_RISKS.HIGH_RISK ||
							impact?.status === FASTSHIFT_ASSESSMENT_RISKS.MEDIUM_RISK,
					).length,
					appearance: 'warning',
					icon: StatusWarningIcon,
					iconAppearance: 'orange',
				},
				{
					key: 'passed',
					label: messages.passed,
					value: results.filter(
						({ impact }) => impact?.status === FASTSHIFT_ASSESSMENT_RISKS.LOW_RISK,
					).length,
					appearance: 'success',
					icon: StatusSuccessIcon,
					iconAppearance: 'lime',
				},
			] as const,
		[results],
	);

	return (
		<Box as="section" aria-labelledby={headingId} aria-busy={isLoading}>
			{isLoading && (
				<VisuallyHidden role="status">{formatMessage(messages.loading)}</VisuallyHidden>
			)}
			<Stack space="space.150">
				<Inline spread="space-between" alignBlock="start" shouldWrap space="space.100">
					<Stack space="space.050">
						<Heading as="h2" size="small" id={headingId}>
							{formatMessage(messages.sectionTitle)}
						</Heading>
						{isLoading ? (
							<Skeleton
								width="154px"
								height="14px"
								borderRadius={token('radius.full')}
								testId="assessment-overview-date-skeleton"
							/>
						) : (
							<Text>{formatMessage(messages.assessedAt, { date })}</Text>
						)}
					</Stack>
					{isLoading ? (
						<Skeleton
							width="102px"
							height="16px"
							borderRadius={token('radius.full')}
							testId="assessment-overview-status-skeleton"
						/>
					) : (
						<Lozenge appearance={status.appearance}>{formatMessage(status.message)}</Lozenge>
					)}
				</Inline>
				<Grid xcss={styles.grid}>
					{metrics.map(({ key, label, value, appearance, icon, iconAppearance }) => (
						<AssessmentOverviewCard
							key={key}
							label={formatMessage(label)}
							value={formatNumber(value)}
							appearance={appearance}
							icon={icon}
							iconAppearance={iconAppearance}
							isLoading={isLoading}
							testId={`assessment-overview-${key}`}
						/>
					))}
				</Grid>
			</Stack>
		</Box>
	);
};
